/**
 * Authorization code flow with PKCE, against Entra ID.
 *
 * Ported from tools/sso-check, which is where this was written and validated live with IT.
 * The reasoning in that tool's comments is preserved because it is the reasoning that
 * matters, and it was learned the expensive way.
 *
 * Written out rather than pulled from MSAL, for two reasons that still hold. The requests
 * this makes are the ones IT asked us to be able to show them, and a library that abstracts
 * them away works against that. And it keeps a CDN off the critical path of signing in to an
 * internal tool.
 *
 * Requires a secure context for crypto.subtle. http://localhost is a secure context by spec,
 * so this works over plain HTTP locally with no certificate.
 */

export interface EntraConfig {
  readonly tenantId: string;
  readonly clientId: string;
  /**
   * The Application ID URI IT settled on - `api://plan-forge`, or `api://<client-id>` if the
   * tenant refused an unverified domain. The API validates the token audience against it, so
   * a wrong value here produces a token the API rejects rather than a failure to sign in.
   */
  readonly applicationIdUri: string;
  readonly redirectUri: string;
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** RFC 7636 allows 43 to 128 characters. 32 random bytes gives 43. */
export function createVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** S256, not plain. Entra accepts both; plain defeats the point of PKCE. */
export async function challengeFromVerifier(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}

export function createState(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/**
 * The scope string that gets an access token for our own API.
 *
 * Requesting only openid and profile returns an ID token and no usable access token, which is
 * the most common reason an API sees nothing it can validate.
 *
 * offline_access is what makes a refresh token come back. Without it every reload is a full
 * redirect through Entra, which for a tool somebody keeps open all day is the difference
 * between invisible and irritating.
 */
export function scopeFor(applicationIdUri: string, scopeName = "access"): string {
  const base = applicationIdUri.replace(/\/+$/, "");
  return `openid profile offline_access ${base}/${scopeName}`;
}

export function authorizeUrl(
  config: EntraConfig,
  state: string,
  challenge: string,
): string {
  const url = new URL(
    `https://login.microsoftonline.com/${config.tenantId}/oauth2/v2.0/authorize`,
  );
  url.search = new URLSearchParams({
    client_id: config.clientId,
    response_type: "code",
    redirect_uri: config.redirectUri,
    response_mode: "query",
    scope: scopeFor(config.applicationIdUri),
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  }).toString();
  return url.toString();
}

export interface TokenResponse {
  readonly access_token: string;
  readonly expires_in: number;
  readonly refresh_token?: string;
  readonly id_token?: string;
  readonly token_type: string;
  readonly scope?: string;
}

export class EntraError extends Error {
  readonly details: unknown;

  constructor(message: string, details: unknown) {
    super(message);
    this.name = "EntraError";
    this.details = details;
  }
}

async function postToken(
  tenantId: string,
  body: URLSearchParams,
): Promise<TokenResponse> {
  const response = await fetch(
    `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/token`,
    {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
    },
  );

  const payload: unknown = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = payload as { error_description?: string; error?: string };
    throw new EntraError(
      detail.error_description ?? detail.error ?? `Token endpoint returned ${response.status}`,
      payload,
    );
  }
  return payload as TokenResponse;
}

/**
 * No client secret: this is a public client. The token endpoint returns CORS headers only when
 * the calling origin matches a redirect URI registered under the single-page application
 * platform. A CORS error here is therefore a registration symptom rather than a code one - the
 * URI is probably registered under Web instead of SPA.
 */
export async function exchangeCode(
  config: EntraConfig,
  code: string,
  verifier: string,
): Promise<TokenResponse> {
  return postToken(
    config.tenantId,
    new URLSearchParams({
      client_id: config.clientId,
      grant_type: "authorization_code",
      code,
      redirect_uri: config.redirectUri,
      scope: scopeFor(config.applicationIdUri),
      code_verifier: verifier,
    }),
  );
}

export async function refreshTokens(
  config: EntraConfig,
  refreshToken: string,
): Promise<TokenResponse> {
  return postToken(
    config.tenantId,
    new URLSearchParams({
      client_id: config.clientId,
      grant_type: "refresh_token",
      refresh_token: refreshToken,
      scope: scopeFor(config.applicationIdUri),
    }),
  );
}

/** The issuer an API Gateway JWT authorizer has to be configured with, for this tenant. */
export function issuerForTenant(tenantId: string): string {
  return `https://login.microsoftonline.com/${tenantId}/v2.0`;
}

export interface AccessTokenClaims {
  readonly aud?: string;
  readonly iss?: string;
  readonly exp?: number;
  readonly roles?: readonly string[];
  readonly scp?: string;
  readonly name?: string;
  readonly preferred_username?: string;
}

/**
 * Reads the claims without verifying the signature.
 *
 * That is not a shortcut: the browser has no business deciding whether a token is valid. The
 * API Gateway authorizer verifies signature, issuer, audience and expiry, and the interface
 * reads the payload only to show who is signed in and to know when to refresh. Anything the
 * browser decided here would be advisory anyway.
 */
export function readClaims(accessToken: string): AccessTokenClaims | null {
  const parts = accessToken.split(".");
  if (parts.length !== 3 || parts[1] === undefined) return null;
  try {
    const padded = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(padded + "=".repeat((4 - (padded.length % 4)) % 4))) as AccessTokenClaims;
  } catch {
    return null;
  }
}
